import type { BibleHighlight } from '@/types/highlight';
import { getContentOwnerBinding } from '@/lib/content-owner';
import {
  completeHighlightDocument,
  highlightFromCompleteDocument,
} from '@/lib/content-lifecycle/contracts';
import {
  authorDeleteMutation,
  authorHighlightDeleteBatch,
  authorHighlightLiveBatchCas,
  CONTENT_LIFECYCLE_GENERATION,
} from '@/lib/content-lifecycle/store';
import { syncContentLifecycle, type ContentSyncResult } from '@/lib/content-lifecycle/sync';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import type { CompleteHighlightDocument } from '@/types/content-lifecycle';

/** Compatibility facade: all three content types share one lifecycle sync. */
export async function syncHighlights(): Promise<ContentSyncResult> {
  if (isCoreReadOnly()) {
    return { pushed: 0, replayed: 0, pulled: 0, conflicts: 0, held: false };
  }
  return syncContentLifecycle();
}

export async function pushHighlight(highlight: BibleHighlight): Promise<boolean> {
  if (!(await queueHighlightMutation(highlight))) return false;
  await syncContentLifecycle();
  return true;
}

export async function queueHighlightMutation(highlight: BibleHighlight): Promise<boolean> {
  if (isCoreReadOnly()) return false;
  const ownerId = getAuthUserId();
  if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
    throw new Error('highlight-owner-context-stale');
  }
  const context = {
    ownerId,
    authEpoch: getAuthEpoch(),
    generation: CONTENT_LIFECYCLE_GENERATION,
  };
  if (highlight.deletedAt) {
    await authorDeleteMutation(context, 'highlight', highlight.id, highlight.deletedAt);
  } else {
    await queueHighlightSetMutations([highlight]);
  }
  return true;
}

export async function queueHighlightSetMutations(
  fallbacks: BibleHighlight[],
): Promise<BibleHighlight[]> {
  if (isCoreReadOnly()) throw new Error('highlight-durable-commit-rejected');
  const ownerId = getAuthUserId();
  if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
    throw new Error('highlight-owner-context-stale');
  }
  const context = {
    ownerId,
    authEpoch: getAuthEpoch(),
    generation: CONTENT_LIFECYCLE_GENERATION,
  };
  const documents = await authorHighlightLiveBatchCas(
    context,
    fallbacks.map((fallback) => {
      const fallbackDocument = completeHighlightDocument(fallback);
      return {
        entityId: fallback.id,
        computeNext: (current: CompleteHighlightDocument | null, installed: unknown) => {
          const installedEnvelope = installed as {
            payload?: { kind?: string; predecessorLiveSnapshot?: CompleteHighlightDocument };
          } | null;
          const acceptedBeforeDelete =
            installedEnvelope?.payload?.kind === 'tombstone'
              ? installedEnvelope.payload.predecessorLiveSnapshot ?? null
              : null;
          const base = current ?? acceptedBeforeDelete ?? fallbackDocument;
          return {
            ...base,
            bookSlug: fallbackDocument.bookSlug,
            chapter: fallbackDocument.chapter,
            verse: fallbackDocument.verse,
            family: fallbackDocument.family,
            sublabel: fallbackDocument.sublabel,
            reference: fallbackDocument.reference,
            updatedAt: fallbackDocument.updatedAt,
            deletedAt: null,
          };
        },
      };
    }),
  );
  return documents.map((document) =>
    highlightFromCompleteDocument(document as CompleteHighlightDocument));
}

export async function queueHighlightDeleteMutations(
  ids: string[],
  tombstonedAt: string,
): Promise<string[]> {
  if (isCoreReadOnly()) throw new Error('highlight-durable-commit-rejected');
  const ownerId = getAuthUserId();
  if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
    throw new Error('highlight-owner-context-stale');
  }
  return authorHighlightDeleteBatch(
    {
      ownerId,
      authEpoch: getAuthEpoch(),
      generation: CONTENT_LIFECYCLE_GENERATION,
    },
    ids,
    tombstonedAt,
  );
}

export function requestHighlightLifecycleSync(): void {
  void syncContentLifecycle().catch(() => {
    // The durable tuple is already committed; reconnect retries it.
  });
}

export function initHighlightOutbox(): void {
  if (isCoreReadOnly()) return;
  // initOutbox owns the one shared listener and awaited single-flight.
}
